import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { createProject } from '../lifeos/api.js';
import { humanize, shortDate, ymdInHomeZone, daysBetween } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { options, text } from '../lifeos/forms.js';
import type { Project, ProjectKind, Sphere } from '../lifeos/types.js';
import { PRIORITY_LABEL, type ProjectSummary } from '../lib/workspaces.js';

/**
 * Project card (spec 2026-10-14): name, status and priority pills, target
 * date, open / waiting counts, sub-projects listed small. The whole card opens
 * the project; each sub-project is its own link. Shared by the Workspace page
 * and the Projects overview (W2 may reuse it).
 */
export function ProjectCard({ project: p, color, today }: { project: ProjectSummary; color?: string | null; today?: string }): JSX.Element {
  const now = today ?? ymdInHomeZone(new Date());
  const late = p.targetDate ? daysBetween(now, p.targetDate.slice(0, 10)) < 0 : false;
  const kids = p.children ?? [];
  return (
    <article
      className={`pc${p.status !== 'active' ? ' pc--muted' : ''}`}
      style={{ '--job': color ?? 'var(--accent)' } as CSSProperties}
      aria-label={p.name}
    >
      <div className="pc__top">
        <h3 className="pc__name">
          <Link to={`/projects/${encodeURIComponent(p.id)}`} className="pc__link">
            {p.name}
          </Link>
        </h3>
        <span className="pc__pills">
          {p.status !== 'active' && <span className="pc-pill pc-pill--status">{humanize(p.status)}</span>}
          {p.priority === 1 && <span className="pc-pill pc-pill--high">High</span>}
          {p.priority === 3 && <span className="pc-pill pc-pill--low">{PRIORITY_LABEL[3]}</span>}
        </span>
      </div>
      <div className="pc__stats">
        <span className={`pc__stat${p.openItems ? ' pc__stat--on' : ''}`}>
          <b className="num">{p.openItems ?? 0}</b> open
        </span>
        {p.waiting > 0 && (
          <span className="pc__stat">
            <b className="num">{p.waiting}</b> waiting
          </span>
        )}
        {p.targetDate && (
          <span className={`pc__stat pc__due${late ? ' pc__due--late' : ''}`}>
            {late ? 'Was due' : 'Due'} {shortDate(p.targetDate.slice(0, 10), now)}
          </span>
        )}
      </div>
      {kids.length > 0 && (
        <ul className="pc__kids" aria-label={`${p.name} sub-projects`}>
          {kids.map((c) => (
            <li key={c.id}>
              <Link to={`/projects/${encodeURIComponent(c.id)}`} className="pc__kid">
                {c.name}
                {c.openItems > 0 && <span className="pc__kidcount num">{c.openItems}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

/** A responsive grid of project cards. */
export function ProjectGrid({ projects, color }: { projects: readonly ProjectSummary[]; color?: string | null }): JSX.Element {
  const today = ymdInHomeZone(new Date());
  return (
    <div className="pc-grid">
      {projects.map((p) => (
        <ProjectCard key={p.id} project={p} color={color} today={today} />
      ))}
    </div>
  );
}

/**
 * Plain `Project` rows → summary trees, taking counts from `known` (a
 * workspace payload) when the server sent them. A sub-project whose parent is
 * not in the list is top level.
 */
export function summarize(projects: readonly Project[], known?: ReadonlyMap<string, ProjectSummary>): ProjectSummary[] {
  const ids = new Set(projects.map((p) => p.id));
  const node = new Map<string, ProjectSummary>();
  for (const p of projects) {
    const k = known?.get(p.id);
    const extra = p as Project & { priority?: number; targetDate?: string | null };
    node.set(p.id, {
      id: p.id,
      slug: p.slug,
      name: p.name,
      sphereKey: p.sphereKey,
      parentId: p.parentId,
      kind: p.kind,
      status: p.status,
      priority: k?.priority ?? extra.priority ?? 2,
      targetDate: k?.targetDate ?? extra.targetDate ?? null,
      openItems: k?.openItems ?? 0,
      waiting: k?.waiting ?? 0,
      nextDue: k?.nextDue ?? null,
      children: [],
    });
  }
  const roots: ProjectSummary[] = [];
  for (const p of projects) {
    const n = node.get(p.id)!;
    if (p.parentId && ids.has(p.parentId) && p.parentId !== p.id) node.get(p.parentId)!.children.push(n);
    else roots.push(n);
  }
  return roots;
}

/** Index a summary tree by id (every depth). */
export function indexSummaries(nodes: readonly ProjectSummary[], into = new Map<string, ProjectSummary>()): Map<string, ProjectSummary> {
  for (const n of nodes) {
    into.set(n.id, n);
    indexSummaries(n.children ?? [], into);
  }
  return into;
}

/** The server's SLUG_RE (spec §3.2), checked here so the form can explain itself. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
const KINDS: ProjectKind[] = ['client_site', 'app', 'internal', 'personal', 'other'];
const slugify = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

/** The "Add a project" sheet, optionally preset to a workspace (sphere) and parent. */
export function NewProjectSheet({
  spheres,
  sphereKey,
  parents = [],
  onClose,
  onCreated,
}: {
  spheres: readonly Sphere[];
  sphereKey?: string;
  /** Possible parents (top-level projects of the workspace) for a sub-project. */
  parents?: readonly { id: string; name: string }[];
  onClose: () => void;
  onCreated: (p: Project) => void;
}): JSX.Element {
  const [initial] = useState(() => ({ kind: 'client_site', sphereKey: sphereKey ?? 'rova', parentId: '' }));
  return (
    <FormSheet
      title="Add a project"
      submitLabel="Add"
      fields={[
        { key: 'name', label: 'Name', required: true, wide: true },
        { key: 'kind', label: 'Kind', kind: 'select', options: options(KINDS, humanize) },
        ...(sphereKey
          ? []
          : [{ key: 'sphereKey', label: 'Workspace', kind: 'select' as const, options: spheres.map((s) => ({ value: s.key, label: s.name })) }]),
        ...(parents.length > 0
          ? [{ key: 'parentId', label: 'Under', kind: 'select' as const, options: [{ value: '', label: 'Nothing (top level)' }, ...parents.map((p) => ({ value: p.id, label: p.name }))] }]
          : []),
        { key: 'slug', label: 'Short name', hint: 'lowercase letters, digits and dashes; blank = from the name' },
        { key: 'prodUrl', label: 'Live site', placeholder: 'https://…', wide: true },
      ]}
      initial={initial}
      onClose={onClose}
      onSubmit={async (v) => {
        const name = text(v.name)!;
        const slug = text(v.slug) ?? slugify(name);
        if (!SLUG_RE.test(slug)) throw new Error('Short name: lowercase letters, digits and dashes only.');
        const created = await createProject({
          name,
          slug,
          kind: v.kind as ProjectKind,
          sphereKey: sphereKey ?? (v.sphereKey || undefined),
          prodUrl: text(v.prodUrl),
          ...(v.parentId ? { parentId: v.parentId } : {}),
        });
        onCreated(created);
      }}
    />
  );
}
